import { useCallback, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { DatePicker } from '@bambi/component-library/src/components/inputs/DatePicker/DatePicker';
import { TextInput } from '@bambi/component-library/src/components/inputs/TextInput/TextInput';
import {
  CalendarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { DateTime } from 'luxon';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  MtmBackfillTripsResponse,
  useBrokersMtmTransportationProvidersBackfillTripsCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { Modal } from 'src/common/primitives/Modal';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { adminActions } from '../admin.slice';

interface BackfillTripsResultProps {
  result: MtmBackfillTripsResponse;
}

function BackfillTripsResult({ result }: BackfillTripsResultProps) {
  const hasErrors = result.total_errors > 0;

  return (
    <div className="space-y-4">
      <h4 className="text-lg font-medium text-gray-900">Backfill Results</h4>

      <div className="grid grid-cols-2 gap-4">
        <div className="rounded-md bg-green-50 p-3">
          <p className="text-sm text-green-700">Trips Created</p>
          <p className="text-2xl font-semibold text-green-900">
            {result.total_created}
          </p>
        </div>

        <div className="rounded-md bg-blue-50 p-3">
          <p className="text-sm text-blue-700">Already Existed</p>
          <p className="text-2xl font-semibold text-blue-900">
            {result.total_already_existed}
          </p>
        </div>

        <div className="rounded-md bg-gray-50 p-3">
          <p className="text-sm text-gray-700">Total Found</p>
          <p className="text-2xl font-semibold text-gray-900">
            {result.total_found}
          </p>
        </div>

        <div className="rounded-md bg-gray-50 p-3">
          <p className="text-sm text-gray-700">Pages Fetched</p>
          <p className="text-2xl font-semibold text-gray-900">
            {result.pages_fetched}
          </p>
        </div>
      </div>

      {hasErrors && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
            <div>
              <p className="font-medium text-red-800">
                {result.total_errors} Error{result.total_errors > 1 ? 's' : ''}
              </p>
              {result.error_details.length > 0 && (
                <ul className="mt-2 list-inside list-disc text-sm text-red-700">
                  {result.error_details.slice(0, 5).map((error, idx) => (
                    <li key={idx}>{JSON.stringify(error)}</li>
                  ))}
                  {result.error_details.length > 5 && (
                    <li>...and {result.error_details.length - 5} more</li>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {result.total_duplicates_in_pages > 0 && (
        <p className="text-sm text-gray-500">
          {result.total_duplicates_in_pages} duplicate(s) found in pages
        </p>
      )}

      <div className="text-sm text-gray-500">
        <p>
          Date range:{' '}
          {result.start_date
            ? DateTime.fromISO(result.start_date).toLocaleString(
                DateTime.DATE_MED
              )
            : 'Default'}{' '}
          to{' '}
          {result.end_date
            ? DateTime.fromISO(result.end_date).toLocaleString(DateTime.DATE_MED)
            : 'Default'}
        </p>
      </div>
    </div>
  );
}

interface DateInputWithPickerProps {
  label: string;
  value: DateTime;
  onChange: (date: DateTime) => void;
  minDate?: DateTime;
  maxDate?: DateTime;
  testId: string;
}

function DateInputWithPicker({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  testId,
}: DateInputWithPickerProps) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      <div className="mt-1 flex items-center gap-2">
        <TextInput
          type="date"
          value={value.toFormat('yyyy-MM-dd')}
          onChange={(e) => {
            const parsed = DateTime.fromFormat(e.target.value, 'yyyy-MM-dd');
            if (parsed.isValid) {
              onChange(parsed);
            }
          }}
          data-testid={`${testId}-input`}
        />
        <DatePicker
          triggerElement={
            <Button variant="ghost-icon" dataTestId={`${testId}-picker`}>
              <CalendarIcon className="h-6 w-6" />
            </Button>
          }
          onChange={(date) => {
            if (date) onChange(date);
          }}
          value={value}
          minDate={minDate}
          maxDate={maxDate}
        />
      </div>
    </div>
  );
}

export function BackfillTripsModal() {
  const dispatch = useAppDispatch();
  const providerId = useAppSelector(
    (state) => state.admin.backfillTripsProviderId
  );
  const isOpen = useAppSelector((state) => state.admin.showBackfillTripsModal);

  const [startDate, setStartDate] = useState<DateTime>(
    DateTime.now().startOf('day')
  );
  const [endDate, setEndDate] = useState<DateTime>(
    DateTime.now().startOf('day').plus({ weeks: 4 })
  );
  const [result, setResult] = useState<MtmBackfillTripsResponse | null>(null);

  const [triggerBackfill, { isLoading }] =
    useBrokersMtmTransportationProvidersBackfillTripsCreateMutation();

  const handleClose = useCallback(() => {
    dispatch(adminActions.setShowBackfillTripsModal(false));
    dispatch(adminActions.setBackfillTripsProviderId(null));
    setResult(null);
    setStartDate(DateTime.now().startOf('day'));
    setEndDate(DateTime.now().startOf('day').plus({ weeks: 4 }));
  }, [dispatch]);

  const handleRun = useCallback(async () => {
    if (!providerId) return;

    try {
      const response = await triggerBackfill({
        id: providerId,
        mtmBackfillTripsRequest: {
          start_date: startDate.toISO(),
          end_date: endDate.toISO(),
        },
      }).unwrap();
      setResult(response);
      dispatch(
        show({
          title: 'Backfill complete',
          description: `Created ${response.total_created} trips`,
          type: 'success',
        })
      );
    } catch (error) {
      dispatch(
        show({
          title: 'Failed to backfill',
          description: formatServerError(error),
          type: 'error',
        })
      );
    }
  }, [dispatch, endDate, providerId, startDate, triggerBackfill]);

  const isValidDateRange = endDate > startDate;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      onInteractOutside={handleClose}
      setOpen={handleClose}
      title="Backfill MTM Trips"
    >
      <div className="flex w-full flex-col gap-4 md:min-w-[400px]">
        <h3 className="text-[24px] font-medium leading-6 text-gray-900">
          Backfill MTM Trips
        </h3>

        {result ? (
          <>
            <BackfillTripsResult result={result} />
            <div className="flex justify-end gap-2 border-t pt-4">
              <Button
                onClick={() => setResult(null)}
                variant="secondary"
                dataTestId="backfill-run-another"
              >
                Run Another
              </Button>
              <Button onClick={handleClose} dataTestId="backfill-close">
                Close
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500">
              Pull trips from MTM for the specified date range. Existing trips
              will not be updated.
            </p>

            <div className="grid grid-cols-2 gap-4">
              <DateInputWithPicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                maxDate={endDate}
                testId="backfill-start-date"
              />
              <DateInputWithPicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                minDate={startDate}
                testId="backfill-end-date"
              />
            </div>

            {!isValidDateRange && (
              <p className="text-sm text-red-600">
                End date must be after start date
              </p>
            )}

            <div className="flex justify-end gap-2 border-t pt-4">
              <Button
                onClick={handleClose}
                variant="secondary"
                dataTestId="backfill-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRun}
                disabled={isLoading || !isValidDateRange}
                dataTestId="backfill-run"
              >
                {isLoading ? 'Running...' : 'Run Backfill'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
